<template>
  <div>
    <v-sheet elevation="1">
      <v-toolbar dense color="white">
        <v-toolbar-title>{{ screenTitle }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-dialog v-model="dialog" persistent>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              class="mx-2"
              color="primary"
              v-if="!readOnly"
            >
              「新規登録」はこのボタンを押してください
            </v-btn>
          </template>
          <validation-observer ref="observer">
            <v-form ref="form" v-model="isDisasterValidation">
              <v-card>
                <v-card-title>
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>
                <v-card-text>
                  <v-container>
                    <datetime-picker
                      :title="'発災日（開始）'"
                      :datetime="editedItem.disaster_accdate"
                      :disabled="editedItem.read_only"
                      :selectedDatetime.sync="editedItem.disaster_accdate"
                      :validation="true"
                      :max="now"
                      :is-clear-when-init="true"
                    />
                    <v-row>
                      <v-col cols="7">
                        <validation-provider
                          v-slot="{ errors }"
                          rules="required"
                          name="disaster_name"
                        >
                          <v-text-field
                            v-model="editedItem.disaster_name"
                            label="災害名称"
                            :placeholder="config.PLACEHOLDER_INPUT"
                            :error-messages="errors"
                            v-bind="formControlProps(30)"
                          >
                          </v-text-field>
                        </validation-provider>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="6" md="4" lg="3">
                        <validation-provider name="emerg_cls">
                          <v-select
                            :items="getEmergencyClasses"
                            v-model="editedItem.emerg_cls"
                            label="緊急事態区分"
                            :placeholder="
                              editedItem.read_only
                                ? config.PLACEHOLDER_SP
                                : config.PLACEHOLDER_SELECT
                            "
                            item-text="name"
                            item-value="emergencyClass"
                            return-object
                            v-bind="formControlProps()"
                          >
                            <template v-slot:selection="data">
                              {{ emergencyClassItems(data.item) }}
                            </template>
                            <template v-slot:item="data">
                              {{ emergencyClassItems(data.item) }}
                            </template>
                          </v-select>
                        </validation-provider>
                      </v-col>
                    </v-row>
                    <datetime-picker
                      :title="'ＡＬ更新日時'"
                      :datetime="editedItem.each_emerg_reg_date_al"
                      :disabled="editedItem.read_only"
                      :selectedDatetime.sync="editedItem.each_emerg_reg_date_al"
                      v-show="showUpdatedDatetime(editedItem, 'AL')"
                      :validation="showUpdatedDatetime(editedItem, 'AL')"
                      :max="now"
                      :is-clear-when-init="true"
                    />
                    <datetime-picker
                      :title="'ＳＥ更新日時'"
                      :datetime="editedItem.each_emerg_reg_date_se"
                      :disabled="editedItem.read_only"
                      :selectedDatetime.sync="editedItem.each_emerg_reg_date_se"
                      v-show="showUpdatedDatetime(editedItem, 'SE')"
                      :validation="showUpdatedDatetime(editedItem, 'SE')"
                      :max="now"
                      :is-clear-when-init="true"
                    />
                    <datetime-picker
                      :title="'ＧＥ更新日時'"
                      :datetime="editedItem.each_emerg_reg_date_ge"
                      :disabled="editedItem.read_only"
                      :selectedDatetime.sync="editedItem.each_emerg_reg_date_ge"
                      v-show="showUpdatedDatetime(editedItem, 'GE')"
                      :validation="showUpdatedDatetime(editedItem, 'GE')"
                      :max="now"
                      :is-clear-when-init="true"
                    />
                    <datetime-picker
                      :title="'終結更新日時'"
                      :datetime="editedItem.each_emerg_reg_date_fn"
                      :disabled="editedItem.read_only"
                      :selectedDatetime.sync="editedItem.each_emerg_reg_date_fn"
                      v-show="showUpdatedDatetime(editedItem, 'FN')"
                      :validation="showUpdatedDatetime(editedItem, 'FN')"
                      :max="now"
                      :is-clear-when-init="true"
                    />
                    <v-row>
                      <v-col cols="12" sm="6" md="4" lg="3">
                        <validation-provider
                          v-slot="{ errors }"
                          rules="oneOf:T,P"
                          name="disaster_cls"
                        >
                          <v-radio-group
                            v-model="editedItem.disaster_cls"
                            row
                            :error-messages="errors"
                            :disabled="editedItem.read_only"
                          >
                            <v-radio label="訓練" value="T"></v-radio>
                            <v-radio label="実災害" value="P"></v-radio>
                          </v-radio-group>
                        </validation-provider>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    class="mx-2"
                    color="primary"
                    @click="save"
                    :disabled="
                      !isDisasterValidation ||
                        (isDisasterValidation && isDisabled())
                    "
                    v-if="!editedItem.read_only"
                  >
                    {{ saveBtnText }}
                  </v-btn>
                  <confirm-dialog ref="confirm"></confirm-dialog>
                  <v-btn class="mx-2" color="primary" @click="close">
                    {{ $cancelButtonText(editedItem.read_only) }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-form>
          </validation-observer>
        </v-dialog>
      </v-toolbar>
      <v-container fluid>
        <error-message :error-object="errorObject" />
        <success-message :success-object="successObject" />
        <p style="margin: 5px 0 15px 0">
          災害名称は下の表を参照してください。条件を指定することで一覧から絞り込むことができます。
        </p>
        <v-row>
          <v-col cols="6" md="3">
            <v-select
              v-model="disasterNameFilterValue"
              :items="disasters"
              label="災害名称"
              :placeholder="config.PLACEHOLDER_SELECT"
              item-text="disaster_name"
              item-value="disaster_no"
              return-object
              clearable
              outlined
            >
            </v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6" md="3">
            <v-text-field
              v-model="disasterDatetimeFromFilterValue"
              :placeholder="config.PLACEHOLDER_SELECT_DATE"
              label="発災日（開始）"
              outlined
              clearable
              readonly
              @click:clear="selectedDisasterDateFrom = null"
            >
              <template v-slot:append>
                <date-picker v-model="selectedDisasterDateFrom" />
              </template>
            </v-text-field>
          </v-col>
        </v-row>
        <v-divider class="mb-1"></v-divider>
      </v-container>
      <top-operation-data-table
        ref="topOpeDataTable"
        :headers="headers"
        :items="items"
        class="disaster-table text-truncate"
        :class="{ training: tab === 'tab-training' }"
        locale="ja-jp"
        loading-text="読込中"
        item-key="disaster_no"
        :sort-by="sort.by"
        :sort-desc="sort.desc"
        must-sort
        :fixed-header="true"
        dispMode="nowrap"
      >
        <template v-slot:top="{ pagination, options, updateOptions }">
          <v-row>
            <v-col cols="6">
              <v-tabs v-model="tab" color="black" class="disaster-tab">
                <v-tab href="#tab-training" class="tab-training">訓練</v-tab>
                <v-tab href="#tab-production">実災害</v-tab>
              </v-tabs>
            </v-col>
            <v-col cols="6">
              <v-data-footer
                :pagination="pagination"
                :options="options"
                :items-per-page-options="[5, 10, 15, -1]"
                @update:options="updateOptions"
                items-per-page-text="$vuetify.dataTable.itemsPerPageText"
              />
            </v-col>
          </v-row>
        </template>
        <template v-slot:[`item.disaster_accdate`]="{ item }">
          {{ formatDatetime(item.disaster_accdate) }}
        </template>
        <template v-slot:[`item.emerg_cls`]="{ item }">
          {{ emergencyClassName(item) }}
        </template>
        <template v-slot:[`item.each_emerg_reg_date_al`]="{ item }">
          {{ formatDatetime(item.each_emerg_reg_date_al) }}
        </template>
        <template v-slot:[`item.each_emerg_reg_date_se`]="{ item }">
          {{ formatDatetime(item.each_emerg_reg_date_se) }}
        </template>
        <template v-slot:[`item.each_emerg_reg_date_ge`]="{ item }">
          {{ formatDatetime(item.each_emerg_reg_date_ge) }}
        </template>
        <template v-slot:[`item.each_emerg_reg_date_fn`]="{ item }">
          {{ formatDatetime(item.each_emerg_reg_date_fn) }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            class="mx-2"
            color="primary"
            small
            @click="editItem(item)"
            v-if="hasUpdateAuthorityByItem(item, readOnly)"
          >
            編集
          </v-btn>
          <v-btn
            class="mx-2"
            color="primary"
            small
            @click="deleteItem(item)"
            v-if="isDeletable(item)"
          >
            削除
          </v-btn>
          <v-btn
            class="mx-2"
            color="primary"
            small
            @click="editItem(item)"
            v-if="!hasUpdateAuthorityByItem(item, readOnly)"
          >
            参照
          </v-btn>
        </template>
      </top-operation-data-table>
    </v-sheet>
    <confirm-dialog ref="confirmDelete"></confirm-dialog>
    <v-overlay :value="overlay" opacity="1" color="white">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import Vue from "vue";
import DatePicker from "@/components/DatePicker";
import DatetimePicker from "@/components/DatetimePicker";
import ConfirmDialog from "@/components/ConfirmDialog";
import {
  extend,
  ValidationObserver,
  ValidationProvider,
  setInteractionMode
} from "vee-validate";
import { required, oneOf } from "vee-validate/dist/rules";
import { RepositoryFactory } from "@/js/repository/RepositoryFactory";
import DateUtils from "@/plugins/date-utils";
import Disaster from "@/plugins/disaster";
import config from "@/js/const";
import { ScreenId } from "@/js/value-objects/screen-id";
import { EmergencyClass } from "@/js/value-objects/emergency-class";
import { DisasterClass } from "@/js/value-objects/disaster-class";
import messageUtils from "@/mixins/message-utils";
import { DisasterTranslator } from "@/js/translators/disasterTranslator";
import disasterUtils from "@/mixins/disaster-utils";
import { hasProperty } from "@/js/utils";

Vue.use(DateUtils);
Vue.use(Disaster);

const disasterRepository = RepositoryFactory.get("disaster");
setInteractionMode("eager");
extend("required", {
  ...required,
  message: "必須項目です。"
});
extend("oneOf", {
  ...oneOf,
  message: "必須項目です。"
});

export default {
  mixins: [disasterUtils, messageUtils],
  components: {
    ValidationProvider,
    ValidationObserver,
    ConfirmDialog,
    DatePicker,
    DatetimePicker,
    ErrorMessage: () => import("@/components/ErrorMessage"),
    SuccessMessage: () => import("@/components/SuccessMessage"),
    TopOperationDataTable: () => import("@/components/ui/TopOperationDataTable")
  },
  data: () => ({
    errors: null,
    overlay: false,
    dialog: false,
    dialogDelete: false,
    config: config,
    deleteDisasterNo: null,
    editedIndex: -1,
    editedItem: {
      disaster_no: null, // 災害番号
      disaster_name: "", // 災害名称
      disaster_cls: "none", // 災害区分
      before_disaster_cls: null, // 変更前災害区分
      emerg_cls: "", // 緊急事態区分
      disaster_accdate: null, // 災害発生日
      each_emerg_reg_date_al: null, // AL更新日時
      each_emerg_reg_date_se: null, // SE更新日時
      each_emerg_reg_date_ge: null, // GE更新日時
      each_emerg_reg_date_fn: null, // 終結更新日時
      hash: null, // ハッシュ値
      authcheck: null, // 権限コード
      read_only: false // 参照のみか
    },
    defaultItem: {
      disaster_no: null, // 災害番号
      disaster_name: "", // 災害名称
      disaster_cls: "none", // 災害区分
      before_disaster_cls: null, // 変更前災害区分
      emerg_cls: "", // 緊急事態区分
      disaster_accdate: null, // 災害発生日
      each_emerg_reg_date_al: null, // AL更新日時
      each_emerg_reg_date_se: null, // SE更新日時
      each_emerg_reg_date_ge: null, // GE更新日時
      each_emerg_reg_date_fn: null, // 終結更新日時
      authcheck: null, // 権限コード
      read_only: false // 参照のみか
    },
    selectedEditedDisasterDateFrom: "",
    selectedEditedDisasterDateTo: "",
    disasterNameFilterValue: "",
    selectedDisasterDateFrom: "",
    selectedDisasterDateTo: "",
    emergencyClasses: [],
    panel: [0, 1],
    tab: null,
    disasters: [],
    minutes: [],
    hours: [],
    readOnly: true,
    isDisasterValidation: false, // 登録（更新）ダイアログの入力チェックエラー有無 true なし／false あり
    screenId: "",
    screenTitle: "",
    now: null,
    screen: null
  }),

  computed: {
    headers() {
      return [
        {
          text: "災害No",
          align: "start",
          value: "disaster_no",
          filter: this.disasterNameFilter
        },
        {
          text: "災害名称",
          value: "disaster_name"
        },
        {
          text: "発災日（開始）",
          value: "disaster_accdate",
          filter: this.disasterDatetimeFromFilter
        },
        {
          text: "緊急事態区分",
          value: "emerg_cls"
        },
        {
          text: "ＡＬ更新日時",
          value: "each_emerg_reg_date_al"
        },
        {
          text: "ＳＥ更新日時",
          value: "each_emerg_reg_date_se"
        },
        {
          text: "ＧＥ更新日時",
          value: "each_emerg_reg_date_ge"
        },
        {
          text: "終結更新日時",
          value: "each_emerg_reg_date_fn"
        },
        {
          text: "操作",
          value: "actions",
          sortable: false
        }
      ];
    },

    sort() {
      return {
        by: ["disaster_accdate", "modified_date"],
        desc: [true, true]
      };
    },

    isNew() {
      return this.editedIndex === -1;
    },

    formTitle() {
      let type = "";
      if (this.readOnly) {
        type = "詳細";
      } else if (this.isNew) {
        type = "登録";
      } else if (!this.hasUpdateAuthorityByItem(this.editedItem)) {
        type = "詳細";
      } else {
        type = "編集";
      }
      return `災害名称の${type}`;
    },

    saveBtnText() {
      return this.isNew ? "登録" : "更新";
    },

    getEmergencyClasses() {
      if (this.isNew) {
        return this.emergencyClasses.filter(
          obj => obj.emergencyClass !== EmergencyClass.FN
        );
      }
      return this.emergencyClasses;
    },

    items: function() {
      let disasterClass = DisasterClass.P;
      if (this.tab === "tab-training") {
        disasterClass = DisasterClass.T;
      }
      return this.disasters.filter(
        disaster => disaster.disaster_cls === disasterClass
      );
    },

    disasterDatetimeFromFilterValue: {
      get: function() {
        return this.$dateUtils.toHyphenFromSlash(this.selectedDisasterDateFrom);
      },
      set: function() {}
    },

    disasterDatetimeToFilterValue: {
      get: function() {
        return this.$dateUtils.toHyphenFromSlash(this.selectedDisasterDateTo);
      },
      set: function() {}
    },

    editedDisasterDateFrom: {
      get: function() {
        return this.$dateUtils.toHyphenFromSlash(
          this.selectedEditedDisasterDateFrom
        );
      },
      set: function(value) {
        this.editItem.disaster_datetime_from = value;
      }
    },

    editedDisasterDateTo: {
      get: function() {
        return this.$dateUtils.toHyphenFromSlash(
          this.selectedEditedDisasterDateTo
        );
      },
      set: function() {}
    },

    formControlProps: () => {
      return function(counter = null) {
        return this.$formControlProps(this.editedItem.read_only, counter);
      };
    }
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
    $route: "initialize"
  },

  mounted() {
    this.pageTitle(this.screenTitle);
  },

  created() {
    this.initialize();
  },

  methods: {
    /**
     * 初期表示処理
     */
    async initialize(isAfterSaved = false) {
      this.overlay = true;
      this.now = this.$moment().format(
        config.DATETIME_FORMAT_YYYYMMDDHHMM00_WITH_SLASH_COLON
      );
      const screen = this.$screenOperationAuthority(
        ScreenId.SF0201,
        ScreenId.SF0205
      );
      this.screen = screen;
      this.readOnly = screen.readOnly;
      this.screenId = screen.screenId;
      this.screenTitle = screen.screenTitle;
      await this.$store
        .dispatch("disasterInfo/loadDisasterInfo", this.screenId)
        .catch(error => {
          this.handleError(error, this.screenId, "aggregation-menu");
        });
      this.disasters = this.$store.state.disasterInfo.data.results;
      this.emergencyClasses = EmergencyClass.list();
      this.minutes = this.$dateUtils.getMinutes();
      this.hours = this.$dateUtils.getHours();
      const date = this.$moment().format(
        this.config.DATETIME_FORMAT_YYYYMMDD000000_WITH_SLASH_COLON
      );
      this.editedItem.disaster_accdate = date;
      this.defaultItem.disaster_accdate = date;
      if (isAfterSaved) {
        await Promise.all([
          this.$store.dispatch("disasterInfo/loadDisasterInfo", this.screenId),
          this.$store.dispatch("disasterInfo/loadLatestDisaster", this.screenId)
        ]).catch(error => {
          this.handleError(error, this.screenId, "aggregation-menu");
        });
        this.disasters = this.$store.state.disasterInfo.data.results;
        // メニューの災害名称プルダウンの災害名称情報更新
        const disaster = this.disasters.find(
          obj => obj.disaster_no === this.$disaster.disaster_no
        );
        if (
          !hasProperty(disaster, "emerg_cls") ||
          EmergencyClass.factory(disaster.emerg_cls).isFn()
        ) {
          this.$disaster = this.defaultDisaster();
        } else {
          this.$disaster = this.defaultDisaster(disaster);
        }
      } else {
        // 一覧表の「訓練/実災害」切り換え（初期表示時はメニューで選択した災害に紐づく）
        this.tab = DisasterClass.getTabId(this.$disaster);
        this.disasterNameFilterValue = this.$disaster;
      }
      if (this.disasters) {
        this.disasters.forEach(disaster => {
          if (disaster.emerg_cls_history_list) {
            // 緊急事態区分履歴を緊急事態区分連番の昇順でソート後、
            // 緊急事態区分ごとに緊急事態登録日時を上書きしていくことで、最終行の緊急事態登録日時が採用される
            const histories = DisasterTranslator.sortHistoriesByEmergClsNum(
              disaster.emerg_cls_history_list
            );
            histories.forEach(history => {
              const emergencyClass = EmergencyClass.factory(
                history.each_emerg_cls
              );
              if (emergencyClass.isAl()) {
                disaster.each_emerg_reg_date_al = history.each_emerg_reg_date;
              } else if (emergencyClass.isSe()) {
                disaster.each_emerg_reg_date_se = history.each_emerg_reg_date;
              } else if (emergencyClass.isGe()) {
                disaster.each_emerg_reg_date_ge = history.each_emerg_reg_date;
              } else if (emergencyClass.isFn()) {
                disaster.each_emerg_reg_date_fn = history.each_emerg_reg_date;
              }
            });
          }
        });
      }
      this.overlay = false;
    },

    /**
     * 日付項目（一覧）表示
     */
    formatDatetime(datetime) {
      if (!datetime) {
        return "";
      }
      const formattedDatetime = this.$moment(datetime);
      if (!formattedDatetime.isValid()) {
        return "";
      }
      return formattedDatetime.format("YYYY/MM/DD HH:mm");
    },

    /**
     * 緊急事態区分（一覧）表示
     */
    emergencyClassName(item) {
      return EmergencyClass.factory(item.emerg_cls).toName();
    },

    /**
     * 災害名称編集（参照）画面表示
     */
    editItem(item) {
      this.editedIndex = this.disasters.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.editedItem.before_disaster_cls = this.editedItem.disaster_cls;
      this.editedItem.read_only = !this.hasUpdateAuthorityByItem(
        item,
        this.readOnly
      );
      this.dialog = true;
    },

    /**
     * 災害名称削除処理
     */
    async deleteItem(item) {
      this.editedIndex = this.disasters.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.deleteDisasterNo = item.disaster_no;
      let message =
        "災害No「" +
        item.disaster_no +
        "」の災害名称を削除してもよろしいでしょうか？";
      if (!(await this.deleteConfirm(message))) {
        return false;
      }
      message =
        "災害名称を削除すると、災害に紐づく様々な情報が削除され、元に戻せません。本当に削除してよろしいですか？";
      if (!(await this.deleteConfirm(message))) {
        return false;
      }
      this.overlay = true;
      const isDeleteFlg = true;
      const payload = DisasterTranslator.toPayload(item, true, isDeleteFlg);
      this.clearMessage();
      await disasterRepository
        .update(ScreenId.SF0205, payload)
        .then(response => {
          // 一覧表の「訓練/実災害」タブの切り替え
          this.tab = DisasterClass.TAB_ID_LIST[payload.disaster_cls];
          this.handleSuccess(response);
        })
        .catch(e => {
          //発災日フィルタークリア・ 一覧表の「訓練/実災害」切り換え・検索条件の設定（初期表示時と同様にする）
          this.selectedDisasterDateFrom = "";
          this.tab = DisasterClass.getTabId(this.$disaster);
          this.disasterNameFilterValue = this.$disaster;
          this.handleUpdateError(e);
        });
      await this.closeDelete();
      await this.initialize(true);
      this.overlay = false;
    },

    /**
     * 削除確認ダイアログ表示
     */
    deleteConfirm(message) {
      return this.$refs.confirmDelete.open(message, "削除");
    },

    /**
     * キャンセル（戻る）処理
     */
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    /**
     * 削除ダイアログ閉じる
     */
    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    /**
     * 災害名称登録・更新処理
     */
    async save() {
      const isValid = await this.$refs.observer.validate();
      if (isValid) {
        this.$refs.observer.reset();
        const isSave = await this.isSave();
        if (!isSave) {
          return;
        }
        this.overlay = true;
        const isUpdate = this.editedIndex > -1;
        const payload = DisasterTranslator.toPayload(this.editedItem, isUpdate);
        this.clearMessage();
        if (isUpdate) {
          await disasterRepository
            .update(ScreenId.SF0202, payload)
            .then(response => {
              // フィルター項目のクリア(発災日)
              this.selectedDisasterDateFrom = "";
              // 一覧表の「訓練/実災害」タブの切り替え
              this.tab = DisasterClass.TAB_ID_LIST[payload.disaster_cls];
              this.handleSuccess(response);
            })
            .catch(e => {
              //発災日フィルタークリア・ 一覧表の「訓練/実災害」切り換え・検索条件の設定（初期表示時と同様にする）
              this.selectedDisasterDateFrom = "";
              this.tab = DisasterClass.getTabId(this.$disaster);
              this.disasterNameFilterValue = this.$disaster;
              this.handleUpdateError(e);
            });
        } else {
          await disasterRepository
            .create(ScreenId.SF0203, payload)
            .then(response => {
              // 一覧表の「訓練/実災害」タブの切り替え
              this.tab = DisasterClass.TAB_ID_LIST[payload.disaster_cls];
              // フィルター項目のクリア(災害名称・発災日)
              this.selectedDisasterDateFrom = "";
              this.disasterNameFilterValue = "";
              this.handleSuccess(response);
            })
            .catch(e => {
              //発災日フィルタークリア・ 一覧表の「訓練/実災害」切り換え・検索条件の設定（初期表示時と同様にする）
              this.selectedDisasterDateFrom = "";
              this.tab = DisasterClass.getTabId(this.$disaster);
              this.disasterNameFilterValue = this.$disaster;
              this.handleCreateError(e);
            });
        }
        await this.close();
        await this.initialize(true);
        this.overlay = false;
      }
    },

    /**
     * 日付形式変換処理
     */
    datetimeForSave(date, hour, minute) {
      return this.$moment(
        `${date}${hour}${minute}`,
        this.config.DATETIME_FORMAT_YYYYMMDDHHMM00
      ).format(this.config.DATETIME_FORMAT_YYYYMMDDHHMM00);
    },

    /**
     * 更新確認処理
     */
    isSave() {
      let message = "";
      const beforeDisasterClass = DisasterClass.factory(
        this.editedItem.before_disaster_cls
      );
      const disasterClass = DisasterClass.factory(this.editedItem.disaster_cls);
      const disasterClassName = disasterClass.toName();
      if (this.editedIndex > -1) {
        if (beforeDisasterClass.isTraining() && disasterClass.isProduction()) {
          message = `訓練から実災害に変更されましたが、災害名称を更新しますか？`;
        } else if (
          beforeDisasterClass.isProduction() &&
          disasterClass.isTraining()
        ) {
          message = `実災害から訓練に変更されましたが、災害名称を更新しますか？`;
        } else {
          message = `${disasterClassName}用の災害名称を更新しますか？`;
        }
      } else {
        message = `${disasterClassName}用の災害名称を登録しますか？`;
      }
      return this.$refs.confirm.open(message, this.saveBtnText);
    },

    /**
     * 災害名称フィルタ処理
     */
    disasterNameFilter(value) {
      this.pageFirst();
      if (!this.disasterNameFilterValue) {
        return true;
      }
      if (this.disasterNameFilterValue.disaster_no === "") {
        return true;
      }
      return value === this.disasterNameFilterValue.disaster_no;
    },

    /**
     * 発災日フィルタ処理
     */
    disasterDatetimeFromFilter(value) {
      this.pageFirst();
      if (!this.disasterDatetimeFromFilterValue) {
        return true;
      }
      const paramDate = this.$moment(value, "YYYYMMDDHHmmss").format(
        "YYYYMMDD"
      );
      let dateFrom = null;
      if (this.disasterDatetimeFromFilterValue) {
        dateFrom = this.formatDateForSearch(
          this.disasterDatetimeFromFilterValue
        );
      }
      if (!paramDate) {
        return false;
      }
      if (dateFrom) {
        return paramDate === dateFrom;
      }
    },

    /**
     * データテーブルの1ページ目へ遷移処理
     */
    pageFirst() {
      if (this.$refs.topOpeDataTable) {
        this.$refs.topOpeDataTable.resetPage();
      }
    },

    /**
     * 削除ボタン表示切替
     */
    isDeletable(item) {
      if (!this.hasDeleteAuthorityByItem(item, this.screen.readOnly)) {
        return false;
      }
      if (!this.screen.hasDeleteAuthority) {
        return false;
      }
      return EmergencyClass.factory(item.emerg_cls).isFn();
    },

    /**
     * 日付形式変換
     */
    formatDateForSearch(datetime) {
      const formattedDatetime = this.$moment(new Date(datetime));
      if (formattedDatetime.isValid()) {
        return formattedDatetime.format("YYYYMMDD");
      }
      return null;
    },

    /**
     * 災害名称表示
     */
    emergencyClassItems(item) {
      return EmergencyClass.factory(item.emergencyClass).toName();
    },

    /**
     * 更新日時の表示切替
     */
    showUpdatedDatetime(item, emergencyClass) {
      if (!item.emerg_cls) {
        return false;
      }
      if (typeof item.emerg_cls === "object") {
        return item.emerg_cls.emergencyClass === emergencyClass;
      }
      return item.emerg_cls === emergencyClass;
    },

    /**
     * ボタンの非活性制御（true：非活性、false：活性）
     */
    isDisabled() {
      let disabled = false;
      let emerg_cls = "";
      if (this.editedItem.emerg_cls) {
        if (typeof this.editedItem.emerg_cls === "object") {
          emerg_cls = this.editedItem.emerg_cls.emergencyClass;
        } else {
          emerg_cls = this.editedItem.emerg_cls;
        }
      } else {
        emerg_cls = this.editedItem.emerg_cls;
      }
      const emergencyClass = EmergencyClass.factory(emerg_cls);
      if (
        !this.editedItem.disaster_accdate ||
        !this.editedItem.disaster_name ||
        this.editedItem.disaster_cls === "none" ||
        (emergencyClass.isAl() && !this.editedItem.each_emerg_reg_date_al) ||
        (emergencyClass.isSe() && !this.editedItem.each_emerg_reg_date_se) ||
        (emergencyClass.isGe() && !this.editedItem.each_emerg_reg_date_ge) ||
        (emergencyClass.isFn() && !this.editedItem.each_emerg_reg_date_fn)
      ) {
        disabled = true;
      }
      return disabled;
    }
  }
};
</script>
<style>
.disaster-tab .v-tab {
  background-color: rgba(0, 0, 0, 0.12);
}
</style>
